import { CompositionStage, useComposition, animate, Easing, clamp, lerp } from "@/components/hero/stage";

/* Relationships hero — "the distance closes".
   Drift: five people orbit outward, threads thinning, months piling up.
   Message: one sentence types itself and is sent.
   Return: the thread brightens, one person travels back in, and the week is logged. */

const W = 760, H = 640;
const AMBER = "#E89A4F";
const DEEP = "#B56A22";
const INK = "#33251A";
const FONT = "Outfit, Poppins, system-ui, sans-serif";
const SCENES = [
  { name: "Drift", dur: 5 },
  { name: "Message", dur: 5 },
  { name: "Return", dur: 5 },
];

const CX = 262, CY = 336;
const PEOPLE = [
  { n: "Marko", a: -108, near: 122, far: 208, months: 3 },
  { n: "Ana", a: -34, near: 138, far: 226, months: 7, chosen: true },
  { n: "Dad", a: 58, near: 118, far: 198, months: 2 },
  { n: "Ines", a: 146, near: 130, far: 232, months: 5 },
  { n: "Luka", a: 208, near: 126, far: 244, months: 11 },
];

const fade = (T, a, b, d = 0.55) => Math.min(clamp((T - a) / d, 0, 1), clamp((b - T) / d, 0, 1));
const LINE = "I was thinking about you today.";

function Distance({ accent = AMBER }) {
  const { T: rawT, CUES: rawCues, authoredTotal } = useComposition();
  const CUES = { Drift: 0, Message: 5, Return: 10, ...rawCues };
  const T = Number.isFinite(rawT) ? rawT : 0;
  const total = authoredTotal || 15;
  const A = (o) => animate(o)(T);

  const drift = A({ from: 0, to: 1, start: CUES.Drift + 0.4, end: CUES.Message - 0.4, ease: Easing.easeInOutCubic });
  const back = A({ from: 0, to: 1, start: CUES.Return + 0.5, end: CUES.Return + 2.8, ease: Easing.easeInOutCubic });
  const typed = A({ from: 0, to: LINE.length, start: CUES.Message + 0.6, end: CUES.Message + 2.9, ease: Easing.linear });
  const sent = A({ from: 0, to: 1, start: CUES.Message + 3.3, end: CUES.Message + 4.1, ease: Easing.easeOutCubic });
  const bubbleIn = fade(T, CUES.Message - 0.2, CUES.Return + 1.6, 0.6);
  const replyIn = A({ from: 0, to: 1, start: CUES.Return + 1.4, end: CUES.Return + 2.4, ease: Easing.easeOutCubic });
  const logIn = A({ from: 0, to: 1, start: CUES.Return + 3.0, end: CUES.Return + 4.0, ease: Easing.easeOutCubic });
  const breathe = 1 + Math.sin((T / total) * Math.PI * 6) * 0.02;

  const nodes = PEOPLE.map((p, i) => {
    const rad = (p.a * Math.PI) / 180;
    let r = lerp(p.near, p.far, drift);
    if (p.chosen) r = lerp(r, p.near - 6, back);
    const close = 1 - clamp((r - p.near) / Math.max(1, p.far - p.near), 0, 1);
    return {
      ...p, i,
      x: CX + r * Math.cos(rad),
      y: CY + r * Math.sin(rad),
      close,
      strong: p.chosen ? Math.max(close, back) : close,
      size: p.chosen ? 64 + back * 14 : 56,
      monthsShown: fade(T, CUES.Drift + 1.6 + i * 0.22, CUES.Message + 0.2, 0.5),
    };
  });

  return (
    <div style={{ position: "absolute", inset: 0, fontFamily: FONT }}>
      {/* warm light behind the centre */}
      <div style={{
        position: "absolute", left: CX - 240, top: CY - 240, width: 480, height: 480, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(232,154,79," + (0.16 + back * 0.16) + "), transparent 66%)",
        transform: "scale(" + breathe + ")",
      }} />

      {/* the rings */}
      {[122, 188, 254].map((r, i) => (
        <div key={r} style={{
          position: "absolute", left: CX - r, top: CY - r, width: r * 2, height: r * 2, borderRadius: "50%",
          border: "1px dashed rgba(51,37,26," + (0.16 - i * 0.04) + ")",
        }} />
      ))}

      {/* every thread in one layer, drawn before the people so it can never cross a circle */}
      <svg width={W} height={H} style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}>
        {nodes.map((p) => (
          <line key={p.n} x1={CX} y1={CY} x2={p.x} y2={p.y}
            stroke={p.chosen && back > 0.05 ? accent : INK}
            strokeOpacity={p.chosen ? 0.18 + p.strong * 0.7 : 0.1 + p.close * 0.35}
            strokeWidth={p.chosen ? 1 + back * 3 : 1 + p.close * 1.2}
            strokeLinecap="round" />
        ))}
      </svg>

      {/* you */}
      <div style={{
        position: "absolute", left: CX - 48, top: CY - 48, width: 96, height: 96, borderRadius: "50%",
        background: "linear-gradient(140deg, #3E2C1D, #241A12)", display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 18px 40px -16px rgba(51,37,26,0.55)",
      }}>
        <span style={{ font: "500 18px/1 " + FONT, color: accent }}>you</span>
      </div>

      {/* the people */}
      {nodes.map((p) => {
        const { x, y, strong, size, monthsShown } = p;
        return (
          <div key={p.n}>
            <div style={{
              position: "absolute", left: x - size / 2, top: y - size / 2, width: size, height: size, borderRadius: "50%",
              background: p.chosen && back > 0.1 ? "linear-gradient(140deg, #F0B673, " + DEEP + ")" : "rgba(255,255,255,0.9)",
              border: "1px solid rgba(51,37,26," + (0.08 + strong * 0.1) + ")",
              boxShadow: p.chosen && back > 0.2 ? "0 16px 34px -12px rgba(181,106,34,0.6), 0 0 0 " + back * 8 + "px rgba(232,154,79,0.12)" : "0 12px 26px -14px rgba(51,37,26,0.4)",
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: 0.5 + strong * 0.5,
            }}>
              <span style={{ font: "500 15px/1 " + FONT, color: p.chosen && back > 0.1 ? "#fff" : "rgba(51,37,26,0.75)" }}>{p.n}</span>
            </div>
            {!p.chosen || back < 0.2 ? (
              <div style={{
                position: "absolute", left: x - 40, top: y + size / 2 + 6, width: 80, textAlign: "center",
                font: "400 13px/1 " + FONT, color: "rgba(51,37,26,0.45)", opacity: monthsShown,
              }}>{p.months} months</div>
            ) : null}
          </div>
        );
      })}

      {/* the caption for drift */}
      <div style={{ position: "absolute", left: 0, top: 26, opacity: fade(T, CUES.Drift - 0.4, CUES.Message - 0.2, 0.6) }}>
        <div style={{ font: "500 12.5px/1 " + FONT, letterSpacing: "0.2em", color: "rgba(51,37,26,0.42)" }}>NOBODY LEFT · IT JUST GOT LATER</div>
      </div>

      {/* the message */}
      <div style={{
        position: "absolute", right: 0, top: 74, width: 316, opacity: bubbleIn,
        transform: "translateY(" + (1 - bubbleIn) * 22 + "px)",
      }}>
        <div style={{
          padding: "22px 24px", borderRadius: "24px 24px 24px 8px", background: "#fff",
          border: "1px solid rgba(51,37,26,0.08)", boxShadow: "0 34px 68px -44px rgba(51,37,26,0.7)",
        }}>
          <div style={{ font: "500 11.5px/1 " + FONT, letterSpacing: "0.18em", color: accent }}>TO ANA · 22:41</div>
          <div style={{ font: "400 21px/1.4 " + FONT, color: INK, marginTop: 12, minHeight: 60 }}>
            {LINE.slice(0, Math.round(typed))}
            {typed < LINE.length && sent < 0.1 ? (
              <span style={{ display: "inline-block", width: 2, height: 20, background: DEEP, marginLeft: 2, verticalAlign: "-3px", opacity: Math.sin(T * 8) > 0 ? 1 : 0.15 }} />
            ) : null}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, opacity: sent }}>
            <span style={{ width: 7, height: 7, borderRadius: 4, background: accent }} />
            <span style={{ font: "400 12.5px/1 " + FONT, color: "rgba(51,37,26,0.45)" }}>sent · no ask inside it</span>
          </div>
        </div>

        {/* the reply */}
        <div style={{
          marginTop: 12, marginLeft: 46, padding: "18px 22px", borderRadius: "24px 24px 8px 24px",
          background: "linear-gradient(140deg, " + accent + ", " + DEEP + ")",
          boxShadow: "0 30px 60px -34px rgba(181,106,34,0.8)",
          opacity: replyIn, transform: "translateY(" + (1 - replyIn) * 18 + "px)",
        }}>
          <div style={{ font: "500 11.5px/1 " + FONT, letterSpacing: "0.18em", color: "rgba(255,255,255,0.7)" }}>ANA · NINE MINUTES LATER</div>
          <div style={{ font: "400 19px/1.4 " + FONT, color: "#fff", marginTop: 10 }}>God, I have been meaning to write to you for a year.</div>
        </div>
      </div>

      {/* the week, logged */}
      <div style={{
        position: "absolute", left: 0, bottom: 18, opacity: logIn, transform: "translateY(" + (1 - logIn) * 16 + "px)",
      }}>
        <div style={{ font: "500 12px/1 " + FONT, letterSpacing: "0.18em", color: "rgba(51,37,26,0.42)" }}>ONE OUTWARD ACTION · WEEK 06</div>
        <div style={{ display: "flex", gap: 7, marginTop: 12 }}>
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <span key={i} style={{
              width: 26, height: 26, borderRadius: 8,
              background: i < 6 ? accent : "rgba(51,37,26,0.08)",
              opacity: i === 5 ? logIn : 1,
            }} />
          ))}
        </div>
      </div>

      {/* scene dots */}
      <div style={{ position: "absolute", right: 0, bottom: 22, display: "flex", gap: 7 }}>
        {SCENES.map((s, i) => {
          const on = (i === 0 && T < CUES.Message) || (i === 1 && T >= CUES.Message && T < CUES.Return) || (i === 2 && T >= CUES.Return);
          return <span key={s.name} style={{ width: on ? 22 : 8, height: 8, borderRadius: 4, background: on ? accent : "rgba(51,37,26,0.16)", transition: "width 0.4s" }} />;
        })}
      </div>
    </div>
  );
}

export default function RelationsHero({ accent = AMBER }) {
  return (
    <CompositionStage width={W} height={H} scenes={SCENES} minScale={0.62} maxHeight={640}>
      <Distance accent={accent} />
    </CompositionStage>
  );
}
